import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import React, { useState, useEffect } from "react"
import styles from "../../../menu.module.scss"
import { Col, Row } from "react-bootstrap"
import AppTextField from "components/StyledComponents/AppTextField"
import AppButton from "components/StyledComponents/AppButton"
import StyledLabel from "components/StyledComponents/StyledLabel"
import { useDispatch, useSelector } from "react-redux"
import { generateHistoryAction } from "redux/marketing/actions"
import { enhanceDidYouKnowFieldAction } from "redux/marketing/actions/user"

const HistoryForm = ({ formik, isEdit, saveBeforeAi }) => {
  const runWithSave = (run) => (saveBeforeAi ? saveBeforeAi(formik, run) : run())
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const [aiLoadingKey, setAiLoadingKey] = useState(null)
  const dispatch = useDispatch()
  const { historyData, menu } = useSelector((state) => state.marketing)

  const handleGenerateHistory = () => {
    runWithSave(() => {
      const payload = {
        menu_ids: [menu?.menu_item_ID]
      }
      dispatch(generateHistoryAction(payload))
    })
  }

  const handleFieldEnhance = (subField, currentValue) => {
    if (!menu?.menu_item_ID) return
    runWithSave(() => {
      setAiLoadingKey(subField)
      dispatch(
        enhanceDidYouKnowFieldAction({
          payload: {
            item_id: menu?.menu_item_ID,
            field: subField === "heading" ? "history_heading" : "history_content",
            current_value: currentValue || ""
          },
          onSuccess: (val) => {
            formik.setFieldValue(`item_history.${subField}`, val)
            setAiLoadingKey(null)
          },
          onError: () => setAiLoadingKey(null)
        })
      )
    })
  }

  const renderAiButton = (subField, currentValue) => {
    if (!isEdit || !menu?.menu_item_ID) return null
    const isLoading = aiLoadingKey === subField
    const hasContent = !!(currentValue && currentValue.trim())
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {/* Invisible label-height spacer so the button lines up with the input box
            (each field renders a body1Regular label above its input). */}
        <StyledLabel variant="body1Regular" aria-hidden style={{ visibility: "hidden" }}>
          &nbsp;
        </StyledLabel>
        <AppButton
          styles={{
            height: "50px",
            padding: "0 16px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            minWidth: "132px",
            justifyContent: "center"
          }}
          disabled={isLoading}
          onClick={() => handleFieldEnhance(subField, currentValue)}>
          <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.7 }} />
          {isLoading
            ? hasContent
              ? "Refining..."
              : "Generating..."
            : hasContent
            ? "Refine"
            : "Generate"}
        </AppButton>
      </div>
    )
  }

  useEffect(() => {
    if (historyData?.history_items?.length) {
      const historyItems = historyData?.history_items[0] || []
      if (historyItems?.length) {
        const historyItem = historyItems[0]
        if (historyItem) {
          formik.setFieldValue("item_history.heading", historyItem?.title || "")
          formik.setFieldValue("item_history.content", historyItem?.description || "")
        }
      }
    }
  }, [historyData])

  return (
    <div>
      <Box
        flexDirection="column"
        className={styles.accordian_container}
        sx={{
          backgroundColor: isExpanded
            ? theme.palette.background.main
            : theme.palette.background.default,
          paddingBottom: !isExpanded && "0px !important",
          margin: isExpanded && "36px 0px !important"
        }}>
        <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
          <AccordionSummary className={styles.summary}>
            <Box display="flex" alignItems="center" gap={1}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="bodyLyon">History</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {isEdit && (
              <Row className="w-100 m-0 mt-2">
                <Col lg="12" className="d-flex justify-content-end">
                  <AppButton sx={{ padding: "10px 16px" }} onClick={handleGenerateHistory}>
                    Generate History
                  </AppButton>
                </Col>
              </Row>
            )}
            <Row className="w-100 m-0 mt-4 mb-4">
              <Col lg="12">
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <AppTextField
                      id={`heading`}
                      name={`heading`}
                      label="Heading"
                      isUpperCase={true}
                      type="text"
                      fullWidth
                      placeholder={`Enter Heading`}
                      value={formik.values.item_history?.heading || ""}
                      onChange={(event) => {
                        formik.setFieldValue(`item_history.heading`, event.target.value)
                      }}
                      onBlur={formik.handleBlur(`heading`)}
                      error={Boolean(formik.errors?.item_history?.heading)}
                      helperText={formik.errors?.item_history?.heading}
                    />
                  </div>
                  {renderAiButton("heading", formik.values.item_history?.heading)}
                </div>
              </Col>
              <Col lg="12" className="mt-4">
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <AppTextField
                      id={`content`}
                      name={`content`}
                      label="Content"
                      isUpperCase={true}
                      type="text"
                      fullWidth
                      placeholder={`Enter Content`}
                      value={formik.values.item_history?.content || ""}
                      onChange={(event) => {
                        formik.setFieldValue(`item_history.content`, event.target.value)
                      }}
                      onBlur={formik.handleBlur(`content`)}
                      error={Boolean(formik.errors?.item_history?.content)}
                      helperText={formik.errors?.item_history?.content}
                    />
                  </div>
                  {renderAiButton("content", formik.values.item_history?.content)}
                </div>
              </Col>
            </Row>
          </AccordionDetails>
        </Accordion>
      </Box>
    </div>
  )
}

export default HistoryForm
